import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SupplierCreateRequest,
  SupplierListQuery,
  SupplierListResponse,
  SupplierResponse,
  SupplierUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<SupplierListQuery>) => ['suppliers', 'list', q] as const,
  one: (id: string) => ['suppliers', 'one', id] as const,
};

function toQueryString(q: Partial<SupplierListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.search) params.set('search', q.search);
  if (q.country) params.set('country', q.country);
  if (q.active !== undefined) params.set('active', String(q.active));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useSuppliers(query: Partial<SupplierListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<SupplierListResponse>(`/api/suppliers${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['suppliers', 'one', 'NIL'],
    queryFn: () => api<SupplierResponse>(`/api/suppliers/${id}`),
    enabled: !!id,
  });
}

export function useCreateSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SupplierCreateRequest) =>
      api<SupplierResponse>('/api/suppliers', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
    },
  });
}

export function useUpdateSupplier(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SupplierUpdateRequest) =>
      api<SupplierResponse>(`/api/suppliers/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/suppliers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers', 'list'] });
    },
  });
}
